import React, { useRef, useEffect } from 'react';
import { useCookieConsent } from '../context/CookieConsentContext';
import './CookieBanner.css';

const CookieBanner = () => {
  const { showBanner, acceptAllCookies, rejectAnalyticsCookies } = useCookieConsent();
  const bannerRef = useRef(null);
  const acceptButtonRef = useRef(null);
  const rejectButtonRef = useRef(null);
  const closeButtonRef = useRef(null);

  // Speichern des aktiven Elements vor dem Öffnen des Banners
  useEffect(() => {
    const lastActiveElement = document.activeElement;

    if (showBanner && acceptButtonRef.current) {
      // Fokus auf den ersten Button setzen für bessere Accessibility
      acceptButtonRef.current.focus();
    }

    // Cleanup-Funktion: Fokus zurücksetzen, wenn Banner geschlossen wird
    return () => {
      if (lastActiveElement && 'focus' in lastActiveElement) {
        lastActiveElement.focus();
      }
    };
  }, [showBanner]);

  // Fokus-Trap Implementierung
  useEffect(() => {
    if (!showBanner) return;

    const handleTabKey = e => {
      if (e.key !== 'Tab') return;

      // Alle fokussierbaren Elemente im Banner
      const focusableElements = bannerRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      // Wenn Shift+Tab gedrückt wird und erster fokussierbarer Elemente aktiv ist
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
      // Wenn Tab gedrückt wird und letzter fokussierbarer Element aktiv ist
      else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    };

    // Tab-Fokus verwalten
    document.addEventListener('keydown', handleTabKey);

    return () => {
      document.removeEventListener('keydown', handleTabKey);
    };
  }, [showBanner]);

  // Inaktivieren aller fokussierbaren Elemente außerhalb des Banners
  useEffect(() => {
    if (!showBanner) return;

    // Alle fokussierbaren Elemente auf der Seite außerhalb des Banners
    const outsideElements = Array.from(
      document.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'),
    ).filter(element => !bannerRef.current.contains(element));

    // Speichern der ursprünglichen tabIndex-Werte
    const originalTabIndices = outsideElements.map(element => ({
      element,
      tabIndex: element.getAttribute('tabindex'),
    }));

    // Setzen von tabindex="-1" für alle Elemente außerhalb
    outsideElements.forEach(element => {
      element.setAttribute('tabindex', '-1');
      element.setAttribute('aria-hidden', 'true');
    });

    // Cleanup: Ursprüngliche tabIndex-Werte wiederherstellen
    return () => {
      originalTabIndices.forEach(({ element, tabIndex }) => {
        if (tabIndex === null) {
          element.removeAttribute('tabindex');
        } else {
          element.setAttribute('tabindex', tabIndex);
        }
        element.removeAttribute('aria-hidden');
      });
    };
  }, [showBanner]);

  if (!showBanner) return null;

  const handleKeyDown = event => {
    // Escape-Taste um Banner zu schließen (mit Ablehnung)
    if (event.key === 'Escape') {
      rejectAnalyticsCookies();
    }
  };

  return (
    <div
      className="cookie-banner"
      role="dialog"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-description"
      aria-modal="true"
      onKeyDown={handleKeyDown}
      ref={bannerRef}
    >
      <div className="cookie-banner-overlay" />
      <div className="cookie-banner-content">
        <h2 id="cookie-banner-title" className="cookie-banner-title sr-only">
          Cookie-Einstellungen
        </h2>
        <div id="cookie-banner-description" className="cookie-banner-description">
          <p>
            Wir verwenden auf ruv-hardwareversicherung.assona.com Cookies, um die einwandfreie Funktion unserer Website
            zu gewährleisten und die Zugriffe auf unserer Website zu analysieren. Insoweit sind einige von ihnen
            notwendig, andere hingegen helfen uns, unser Onlineangebot zu verbessern. Dafür geben wir Informationen zu
            Ihrer Verwendung unserer Website an unseren Partner für Analysen (InnoCraft/ Neuseeland), weiter. Ihre
            Einwilligung zur Nutzung von Cookies können Sie jederzeit widerrufen, indem Sie rechts unten auf der Seite
            auf die Datenschutz-Einstellungen klicken und dort die entsprechenden Anpassungen vornehmen. Die Speicherung
            erfolgt dabei aufgrund Ihrer Einwilligung nach Maßgabe von § 25 Abs. 1 TTDSG, die weitere Verarbeitung
            aufgrund Ihrer Einwilligung nach Art. 6 Abs. 1 S. 1 lit. a) DSGVO.
          </p>
        </div>

        <div className="cookie-banner-actions">
          <button
            ref={acceptButtonRef}
            onClick={acceptAllCookies}
            className="cookie-banner-button cookie-banner-button--primary"
            type="button"
            aria-describedby="accept-all-description"
          >
            Alle Cookies akzeptieren
          </button>
          <div id="accept-all-description" className="sr-only">
            Akzeptiert alle Cookies einschließlich Analytics-Cookies für Website-Verbesserungen
          </div>

          <button
            ref={rejectButtonRef}
            onClick={rejectAnalyticsCookies}
            className="cookie-banner-button cookie-banner-button--secondary"
            type="button"
            aria-describedby="reject-analytics-description"
            data-testid="reject-analytics-button"
          >
            Nur notwendige Cookies
          </button>
          <div id="reject-analytics-description" className="sr-only">
            Akzeptiert nur notwendige Cookies für die Grundfunktionen der Website
          </div>
        </div>

        <button
          ref={closeButtonRef}
          onClick={rejectAnalyticsCookies}
          className="cookie-banner-close"
          aria-label="Cookie-Banner schließen und nur notwendige Cookies akzeptieren"
          type="button"
        >
          ✕
        </button>
      </div>
    </div>
  );
};

export default CookieBanner;
